import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CrmClient, CrmClientAddress, CrmClientCommunication } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { Party, PartyId } from '../../party/party.model';
import { PartyType } from '../../party/party-type.enum';

export const CRM_RECEIVABLE_ROLES = ['CUSTOMER'];
export const CRM_PAYABLE_ROLES = [
    'SUPPLIER',
    'WAREHOUSE_SUPPLIER',
    'FUEL_VENDOR',
    'MAINTENANCE_VENDOR',
    'INSURANCE_VENDOR',
    'CUSTOMS_CLEARANCE',
    'TRANSPORTER',
    'BROKER'
];

export interface CrmPartySelection {
    client: CrmClient | null;
    contact?: CrmClientCommunication | null;
}

@Injectable({ providedIn: 'root' })
export class CrmPartyContextService {
    constructor(private crmService: CrmService) {}

    loadClients(companyId: number | undefined, roles: string[]): Observable<CrmClient[]> {
        if (!companyId) return of([]);
        return this.crmService.getClients(companyId, roles).pipe(
            map(clients => (clients || []).sort((a, b) => this.clientName(a).localeCompare(this.clientName(b)))),
            catchError(() => of([]))
        );
    }

    loadContacts(clientId: number | undefined, companyId?: number): Observable<CrmClientCommunication[]> {
        return this.loadContactsStrict(clientId, companyId).pipe(catchError(() => of([])));
    }

    loadContactsStrict(clientId: number | undefined, companyId?: number): Observable<CrmClientCommunication[]> {
        if (!clientId) return of([]);
        return this.crmService.getClientMasterCommunications(clientId, companyId).pipe(
            map(contacts => contacts || [])
        );
    }

    loadAddress(clientId: number | undefined): Observable<CrmClientAddress | null> {
        if (!clientId) return of(null);
        return this.crmService.getClientAddress(clientId).pipe(catchError(() => of(null)));
    }

    preferredContact(contacts: CrmClientCommunication[], selectedContactId?: number | null): CrmClientCommunication | null {
        if (!contacts?.length) return null;
        return (selectedContactId ? contacts.find(contact => contact.id === selectedContactId) : null)
            || contacts.find(contact => contact.primaryRecord)
            || contacts[0]
            || null;
    }

    partyFromClient(client?: CrmClient | null): Party | null {
        if (!client) return null;
        const partyId = this.partyIdFromClient(client);
        if (!partyId) return null;
        return {
            id: partyId,
            entityName: this.clientName(client),
            emailAddress: client.primaryEmail,
            phoneNumber: client.phoneNumber || client.mobileNumber,
            isCompany: partyId.entityType === PartyType.Company,
            address: this.clientAddressLine(client),
            crNumber: client.crNumber != null ? String(client.crNumber) : undefined,
            taxRegNumber: client.taxRegNumber,
            owningCompanyId: client.companyId
        };
    }

    partyIdFromClient(client?: CrmClient | null): PartyId | null {
        if (!client) return null;
        if (client.linkedCompanyId) {
            return { entityId: client.linkedCompanyId, entityType: PartyType.Company };
        }
        if (client.linkedCustomerId) {
            return { entityId: client.linkedCustomerId, entityType: PartyType.Customer };
        }
        if (client.linkedTransportationAgencyId) {
            return { entityId: client.linkedTransportationAgencyId, entityType: PartyType.TransportationAgency };
        }
        return null;
    }

    clientName(client?: CrmClient | null): string {
        return client?.companyName || client?.customerNameLocal || client?.clientNumber || '-';
    }

    contactName(contact?: CrmClientCommunication | null): string {
        return contact?.name || contact?.email || contact?.phone || contact?.mobile || '-';
    }

    displayName(record: any): string {
        return record?.crmDisplayName
            || record?.crmClient?.companyName
            || record?.crmClientName
            || record?.mainParty?.entityName
            || record?.subParty?.entityName
            || '-';
    }

    contactDisplayName(record: any): string {
        return record?.crmContactDisplayName
            || record?.crmClientContact?.name
            || record?.crmContactName
            || record?.crmContactEmail
            || record?.crmContactPhone
            || '-';
    }

    addressDisplay(record: any): string {
        return record?.crmClientAddress?.formattedAddress
            || record?.crmAddress
            || record?.mainParty?.address
            || record?.subParty?.address
            || '-';
    }

    private clientAddressLine(client: CrmClient): string | undefined {
        return [client.postalCode].filter(Boolean).join(', ') || undefined;
    }
}
